/**
 * Le modele generique. Ajouter un type d'item = ajouter une variante a
 * `PayloadParType` ci-dessous. Aucune migration SQL n'est requise : la colonne
 * `payload` de D1 est un simple TEXT contenant du JSON.
 */

export type Statut = 'todo' | 'fait' | 'annule';
export type Source = 'manuel' | 'email';

/** Un identifiant de membre. Alias nomme pour la lisibilite des signatures. */
export type MembreId = string;

/**
 * Les types d'items. `tache`, `rdv` et `course` ont une interface ; `repas`
 * et `vetement` sont declares pour que le schema et le protocole les acceptent
 * deja, mais n'ont aucun ecran.
 *
 * Ajouter `course` n'a demande aucune migration : c'est une variante de plus
 * dans cette union et un payload dedie. La table `items` n'a pas bouge. Un item d'un type inconnu du front est ignore a l'affichage,
 * jamais rejete par le serveur : un client plus vieux ne doit pas bloquer un
 * client plus recent.
 */
export type ItemType = 'tache' | 'rdv' | 'course' | 'repas' | 'vetement';

export interface PayloadTache {
  /** Texte libre, saisi dans la fiche. */
  notes?: string;
  /** Sous-taches simples, pour plus tard. */
  etapes?: { titre: string; faite: boolean }[];
}

export interface PayloadCourse {
  /** « 2 L », « 500 g » — libre, jamais interprete. */
  quantite?: string;
  notes?: string;
}

export interface PayloadRdv {
  /** Heure de fin, ISO8601. `du_le` porte l'heure de debut. */
  fin_le?: string;
  lieu?: string;
  /** Minutes avant `du_le`. Non utilise en v1, le brief est quotidien. */
  rappel_minutes?: number;
  notes?: string;
}

export interface PayloadRepas {
  repas?: 'dejeuner' | 'diner' | 'souper';
  portions?: number;
  ingredients?: string[];
}

export interface PayloadVetement {
  taille?: string;
  saison?: 'hiver' | 'printemps' | 'ete' | 'automne';
  /** L'enfant concerne. Present sur tout element de l'ecran Enfants. */
  pour?: MembreId;
}

/**
 * Les categories de l'ecran Enfants. Chacune n'est qu'un `type` d'item de
 * plus — aucune table, aucune colonne, conformement au principe de la table
 * generique. « Autre » reutilise `tache` a dessein : une sortie scolaire est
 * une vraie tache, qui doit aussi apparaitre dans le brief et l'agenda.
 */
export const CATEGORIES_ENFANT = [
  { type: 'vetement', libelle: 'Habillage' },
  { type: 'repas', libelle: 'Lunch' },
  { type: 'tache', libelle: 'Autre' },
] as const satisfies readonly { type: ItemType; libelle: string }[];

/** Types propres a la routine enfantine, exclus de l'agenda familial. */
export const TYPES_ROUTINE_ENFANT: readonly ItemType[] = ['vetement', 'repas'];

/** L'enfant concerne par un item, s'il y en a un. */
export function pourEnfant(item: { payload: unknown }): string | null {
  const p = item.payload as { pour?: unknown } | null;
  return typeof p?.pour === 'string' ? p.pour : null;
}

export interface PayloadParType {
  tache: PayloadTache;
  rdv: PayloadRdv;
  course: PayloadCourse;
  repas: PayloadRepas;
  vetement: PayloadVetement;
}

/** Un item tel qu'il circule sur le reseau et vit en memoire. */
export interface Item<T extends ItemType = ItemType> {
  id: string;
  famille_id: string;
  type: T;
  titre: string;
  statut: Statut;
  assigne_a: MembreId | null;
  /** ISO8601. Date seule (2026-09-19) si pas d'heure, instant complet sinon. */
  du_le: string | null;
  /** RRULE RFC 5545. Stocke mais non interprete en v1. */
  recurrence: string | null;
  payload: PayloadParType[T];
  cree_par: MembreId;
  source: Source;
  complete_par: MembreId | null;
  complete_le: string | null;
  cree_le: string;
  /** Horloge du last-write-wins. Toujours fixee par le serveur. */
  maj_le: string;
}

export type Tache = Item<'tache'>;
export type Rdv = Item<'rdv'>;

/** Champs qu'un client a le droit de proposer a la creation. */
export interface BrouillonItem {
  type: ItemType;
  titre: string;
  statut?: Statut;
  assigne_a?: MembreId | null;
  du_le?: string | null;
  recurrence?: string | null;
  payload?: Record<string, unknown>;
}

/** Champs qu'un client a le droit de modifier. */
export interface ChangementItem {
  titre?: string;
  statut?: Statut;
  assigne_a?: MembreId | null;
  du_le?: string | null;
  recurrence?: string | null;
  payload?: Record<string, unknown>;
}

export const CHAMPS_MODIFIABLES = [
  'titre',
  'statut',
  'assigne_a',
  'du_le',
  'recurrence',
  'payload',
] as const satisfies readonly (keyof ChangementItem)[];

export const STATUTS: readonly Statut[] = ['todo', 'fait', 'annule'];

export function estStatut(v: unknown): v is Statut {
  return typeof v === 'string' && (STATUTS as readonly string[]).includes(v);
}

/**
 * Departage deux versions du meme item. Last-write-wins sur `maj_le`, avec
 * l'identifiant du membre en cas d'egalite a la milliseconde — sans ce
 * second critere, deux appareils peuvent rester durablement divergents.
 */
export function gagnant<T extends Item>(a: T, b: T): T {
  if (a.maj_le !== b.maj_le) return a.maj_le > b.maj_le ? a : b;
  const ma = a.complete_par ?? a.cree_par;
  const mb = b.complete_par ?? b.cree_par;
  return ma >= mb ? a : b;
}

/**
 * Une occurrence de la meme serie existe-t-elle deja a cette echeance ?
 *
 * Garde-fou de la recurrence, ajoute apres un vrai degat en production : un
 * cours de gymnastique hebdomadaire s'est retrouve en double sur deux
 * dimanches, dont un deja coche une semaine a l'avance.
 *
 * La chaine des occurrences n'avance que lorsqu'on coche, et `engendre_par`
 * permet de defaire une generation — mais seulement si l'occurrence engendree
 * est restee intacte. Cocher celle-ci avant de decocher son parent fait
 * **bifurquer** la chaine : l'ancienne branche survit, la nouvelle demarre, et
 * les deux produisent une occurrence a la meme date.
 *
 * Plutot que de tenter de demeler les filiations, on verifie la seule chose
 * qui compte a l'arrivee : il n'y a jamais deux fois la meme chose au meme
 * moment. Les items annules ne comptent pas — ce sont des traces.
 */
export function occurrenceDejaPresente(
  items: Iterable<Item>,
  modele: Pick<Item, 'id' | 'type' | 'titre' | 'recurrence'>,
  echeance: string,
): boolean {
  for (const candidat of items) {
    if (candidat.id === modele.id) continue;
    if (candidat.statut === 'annule') continue;
    if (candidat.type !== modele.type) continue;
    if (candidat.titre !== modele.titre) continue;
    if (candidat.recurrence !== modele.recurrence) continue;
    if (memeEcheance(candidat.du_le, echeance)) return true;
  }
  return false;
}

/**
 * Deux echeances designent-elles le meme moment ?
 *
 * La comparaison se fait sur l'instant et non sur la chaine : « 14:00-04:00 »
 * et « 18:00Z » sont le meme rendez-vous, et rien ne garantit que deux
 * ecritures du meme instant se ressemblent. Les dates sans heure ne se
 * comparent qu'entre elles — un lundi entier et un lundi a 14 h ne sont pas la
 * meme echeance.
 */
function memeEcheance(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return false;
  const aSeuleDate = a.length === 10;
  const bSeuleDate = b.length === 10;
  if (aSeuleDate !== bSeuleDate) return false;
  if (aSeuleDate) return a === b;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
  return ta === tb;
}
